import { theme } from 'lib';

/* ─────────────────────────── Enums (exact string values) ─────────────────── */

export const MODERATION_CATEGORIES = ['Hate', 'Sexual', 'SelfHarm', 'Violence'];

export const CATEGORY_LABELS = {
  Hate: 'Hate',
  Sexual: 'Sexual',
  SelfHarm: 'Self-Harm',
  Violence: 'Violence',
  Profanity: 'Profanity'
};

export const MODERATION_ACTIONS = ['Allow', 'Warn', 'Flag', 'Block'];

export const PROFANITY_LEVELS = ['low', 'medium', 'high'];

export const LEVEL_LABELS = {
  low: 'Low',
  medium: 'Medium',
  high: 'High'
};

/** Synthetic logs-filter value for local word-list hits (not an Azure category). */
export const LOG_CATEGORY_OPTIONS = [...MODERATION_CATEGORIES, 'Profanity'];

export const MODERATION_INPUT_TYPES = ['text', 'image'];

export const MODERATION_CONTENT_TYPES = [
  'Comment',
  'FanChat',
  'Shoutout',
  'Post',
  'ProfileDescription',
  'Username',
  'FanCamPhoto',
  'ProfileImage',
  'Story'
];

/**
 * Azure FourSeverityLevels. `null` on a threshold disables that action for the
 * category. The policy selects choose from 2 / 4 / 6 / Off(null).
 */
export const SEVERITY_LEVELS = [
  { value: 0, label: 'Safe' },
  { value: 2, label: 'Low' },
  { value: 4, label: 'Medium' },
  { value: 6, label: 'High' }
];

/** Options offered in the Warn/Flag/Block threshold selects. */
export const THRESHOLD_OPTIONS = [
  { value: 2, label: '2 · Low' },
  { value: 4, label: '4 · Medium' },
  { value: 6, label: '6 · High' },
  { value: null, label: 'Off' }
];

export const SEVERITY_LABEL = {
  0: 'Safe',
  2: 'Low',
  4: 'Medium',
  6: 'High'
};

/* ─────────────────────────── Colour maps ─────────────────────────────────── */

/**
 * Colour-coded action badge styles (bg / text / border), following the
 * Block=red · Flag=amber · Warn=yellow · Allow=grey spec.
 */
export const ACTION_BADGE = {
  Allow: { bg: '#F1F5F9', col: '#64748B', border: '#E2E8F0', dot: '#94A3B8' },
  Warn: { bg: '#FEFCE8', col: '#CA8A04', border: '#FEF08A', dot: '#EAB308' },
  Flag: { bg: '#FFF7ED', col: '#EA580C', border: '#FED7AA', dot: '#F59E0B' },
  Block: { bg: '#FFF1F2', col: '#E11D48', border: '#FECDD3', dot: '#EF4444' }
};

/** Word-list level badge styles, aligned with severityColor (2/4/6). */
export const LEVEL_BADGE = {
  low: { bg: '#FEFCE8', col: '#CA8A04', border: '#FEF08A', dot: '#EAB308' },
  medium: { bg: '#FFF7ED', col: '#EA580C', border: '#FED7AA', dot: '#F59E0B' },
  high: { bg: '#FFF1F2', col: '#E11D48', border: '#FECDD3', dot: '#EF4444' }
};

/** Colour for a raw severity number (0/2/4/6 or anything in between). */
export const severityColor = (severity) => {
  if (severity == null) return theme.GREY_50;
  if (severity >= 6) return '#E11D48';
  if (severity >= 4) return '#EA580C';
  if (severity >= 2) return '#CA8A04';
  return '#16A34A';
};

/* ─────────────────────────── Helpers ─────────────────────────────────────── */

const S3_BASE = (process.env.REACT_APP_AWS_S3_BASE_URL || '').replace(/\/$/, '');

/** Build a public URL for an S3 key stored on a moderation log / content doc. */
export const s3Url = (key) => {
  if (!key) return null;
  if (/^https?:\/\//i.test(key)) return key;
  return `${S3_BASE}/${String(key).replace(/^\//, '')}`;
};

export const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Resolve a human preview of a flagged item. Prefers what Azure actually
 * analysed (the log's textSnippet / s3Key), falling back to common fields on
 * the resolved content document.
 */
export const resolveContentPreview = (item) => {
  const content = item?.content || {};

  const imageKey =
    item?.s3Key ||
    content.s3Key ||
    content.image ||
    content.media ||
    content.photo ||
    content.url ||
    content.avatar ||
    null;

  const text =
    item?.textSnippet ||
    content.text ||
    content.body ||
    content.message ||
    content.caption ||
    content.description ||
    content.comment ||
    content.content ||
    content.title ||
    content.username ||
    null;

  return {
    isImage: item?.inputType === 'image',
    imageUrl: s3Url(imageKey),
    text,
    deleted: item?.content === null && !!item?.contentRef
  };
};
